import {
  Badge,
  Box,
  Button,
  CircularProgress,
  Drawer,
  Icon,
  IconButton,
  InputAdornment,
  TextField,
  Theme,
  Typography,
} from "@outschool/backpack";
import {
  ThreadsForSectionQuery,
  ThreadsForSectionQueryVariables,
} from "@outschool/gql-frontend-generated";
import { faComment, faPlus, faSearch, faTimes } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { dayjs } from "@outschool/time";
import { useQueryWithPreviousData } from "@outschool/ui-apollo";
import {
  BlockScopedMessageDraft,
  BlockScopedOptimisticThreadCreatedPayload,
  BlockScopedThreadCreateFailedPayload,
} from "@outschool/ui-components-classroom";
import { UserHeadshotImage } from "@outschool/ui-components-shared";
import { useIsMobile } from "@outschool/ui-utils";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useCurrentLearner } from "../../providers/CurrentLearnerProvider";
import {
  PostType,
  useMessageTeacher,
} from "../../providers/MessageTeacherProvider";
import { THREADS_FOR_SECTION_QUERY } from "../../queries/ThreadsForSectionQuery";
import { MESSAGE_TEACHER_POLL_INTERVAL_MS } from "./constants";
import NewThreadView from "./NewThreadView";
import ThreadDetailView from "./ThreadDetailView";
import ThreadListItem from "./ThreadListItem";
import type { MessageData, ThreadViewContext } from "./types";
import {
  getThreadPostTitle,
  isGeneralThread,
  PostTarget,
  postTitleFromThreadOrOptimistic,
} from "./utils";

const VIEW = {
  LIST: "list",
  THREAD: "thread",
  NEW_THREAD: "newThread",
} as const;

type PendingPost =
  | { kind: "post"; postTarget: PostTarget; postTitle: string }
  | { kind: "general" };

type DrawerView =
  | { kind: typeof VIEW.LIST }
  | {
      kind: typeof VIEW.THREAD;
      threadUid: string;
      postTitleOverride: string | null;
    }
  | { kind: typeof VIEW.NEW_THREAD; pendingPost: PendingPost };

interface ComposerRestoreState {
  draft: BlockScopedMessageDraft;
  error: unknown;
}

interface OptimisticThreadState {
  tempThreadUid: string;
  resolvedThreadUid: string | null;
  pendingPost: PendingPost;
  messages: MessageData[];
}

interface MessageTeacherButtonProps {
  sectionUid: string;
}

function useStateWithRef<T>(initialState: T) {
  const [state, setState] = useState<T>(initialState);
  const stateRef = useRef(state);

  const setStateAndRef = useCallback((next: React.SetStateAction<T>) => {
    setState(previousState => {
      const resolvedState =
        typeof next === "function"
          ? (next as (prevState: T) => T)(previousState)
          : next;
      stateRef.current = resolvedState;
      return resolvedState;
    });
  }, []);

  return [state, setStateAndRef, stateRef] as const;
}

function toMessageData(
  message: BlockScopedOptimisticThreadCreatedPayload["optimisticMessage"],
): MessageData {
  return {
    uid: message.uid,
    sentAt: message.sentAt,
    sender: {
      uid: message.sender.uid,
      name: message.sender.name,
      photo: message.sender.photo,
      isLearner: message.sender.isLearner,
    },
    messageContent: {
      uid: message.messageContent.uid,
      text: message.messageContent.text,
      attachments: message.messageContent.attachments,
      video: message.messageContent.video,
    },
  };
}

function pendingPostTarget(pendingPost: PendingPost): PostTarget {
  return pendingPost.kind === "post" ? pendingPost.postTarget : {};
}

export default function MessageTeacherButton({
  sectionUid,
}: MessageTeacherButtonProps) {
  const { t } = useTranslation("learnerApp\\MessageTeacherButton");
  const isMobile = useIsMobile();
  const currentLearner = useCurrentLearner();
  const { registerOpenForPost } = useMessageTeacher();

  const [open, setOpen] = useState(false);
  const [view, setView, viewRef] = useStateWithRef<DrawerView>({
    kind: VIEW.LIST,
  });
  const [composerRestoreState, setComposerRestoreState] =
    useState<ComposerRestoreState | null>(null);
  const [optimisticThread, setOptimisticThread, optimisticThreadRef] =
    useStateWithRef<OptimisticThreadState | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const clearComposerFlowState = useCallback(() => {
    setComposerRestoreState(null);
    setOptimisticThread(null);
  }, [setOptimisticThread]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const {
    data,
    loading: threadsLoading,
    refetch,
  } = useQueryWithPreviousData<
    ThreadsForSectionQuery,
    ThreadsForSectionQueryVariables
  >(THREADS_FOR_SECTION_QUERY, {
    variables: {
      sectionUid,
      searchQuery: debouncedSearch || undefined,
    },
    fetchPolicy: "cache-and-network",
    pollInterval: MESSAGE_TEACHER_POLL_INTERVAL_MS,
  });

  const unreadCount = data?.section?.unreadThreadsCount ?? 0;
  const leader = data?.section?.leader;
  const threads = useMemo(
    () => data?.section?.threads ?? [],
    [data?.section?.threads],
  );
  const sectionEndTime = data?.section?.end_time ?? null;

  const openForPost = useCallback(
    (postUid: string, postType: PostType, postTitle: string) => {
      const match = threads.find(thread => {
        if (postType === "assignmentPost") {
          return thread.learningAssignmentClassPost?.uid === postUid;
        }
        return thread.classPost?.uid === postUid;
      });

      clearComposerFlowState();
      if (match) {
        setView({
          kind: VIEW.THREAD,
          threadUid: match.uid,
          postTitleOverride: null,
        });
      } else {
        const postTarget: PostTarget =
          postType === "classPost"
            ? { classPostUid: postUid }
            : { learningAssignmentClassPostUid: postUid };
        setView({
          kind: VIEW.NEW_THREAD,
          pendingPost: { kind: "post", postTarget, postTitle },
        });
      }
      setOpen(true);
    },
    [clearComposerFlowState, setView, threads],
  );

  useEffect(() => {
    registerOpenForPost(openForPost);
  }, [registerOpenForPost, openForPost]);

  const handleClose = () => {
    setOpen(false);
    setView({ kind: VIEW.LIST });
    clearComposerFlowState();
    setSearchInput("");
    setDebouncedSearch("");
  };

  const handleThreadCreated = useCallback(
    (threadUid: string) => {
      const currentView = viewRef.current;
      const currentOptimisticThread = optimisticThreadRef.current;
      const postTitleOverride =
        currentView.kind === VIEW.NEW_THREAD &&
        currentView.pendingPost.kind === "post"
          ? currentView.pendingPost.postTitle
          : currentOptimisticThread?.pendingPost.kind === "post"
          ? currentOptimisticThread.pendingPost.postTitle
          : null;

      setComposerRestoreState(null);
      setView({ kind: VIEW.THREAD, threadUid, postTitleOverride });

      if (currentOptimisticThread) {
        const isViewingTemporaryThread =
          currentView.kind === VIEW.THREAD &&
          currentView.threadUid === currentOptimisticThread.tempThreadUid;
        if (isViewingTemporaryThread) {
          setOptimisticThread(previous =>
            previous &&
            previous.tempThreadUid === currentOptimisticThread.tempThreadUid
              ? { ...previous, resolvedThreadUid: threadUid }
              : previous,
          );
        } else {
          setOptimisticThread(null);
        }
      }

      refetch();
    },
    [optimisticThreadRef, refetch, setOptimisticThread, setView, viewRef],
  );

  const handleOptimisticThreadCreated = useCallback(
    ({
      threadUid,
      optimisticMessage,
    }: BlockScopedOptimisticThreadCreatedPayload) => {
      const currentView = viewRef.current;
      if (currentView.kind !== VIEW.NEW_THREAD) {
        return;
      }

      const activePendingPost = currentView.pendingPost;
      const postTitleOverride =
        activePendingPost.kind === "post" ? activePendingPost.postTitle : null;

      setComposerRestoreState(null);
      setView({ kind: VIEW.THREAD, threadUid, postTitleOverride });
      setOptimisticThread({
        tempThreadUid: threadUid,
        resolvedThreadUid: null,
        pendingPost: activePendingPost,
        messages: [toMessageData(optimisticMessage)],
      });
    },
    [setOptimisticThread, setView, viewRef],
  );

  const handleThreadCreateFailed = useCallback(
    ({ threadUid, draft, error }: BlockScopedThreadCreateFailedPayload) => {
      const currentOptimisticThread = optimisticThreadRef.current;
      if (
        !currentOptimisticThread ||
        currentOptimisticThread.tempThreadUid !== threadUid
      ) {
        return;
      }

      const currentView = viewRef.current;
      const shouldRollbackComposer =
        currentView.kind === VIEW.THREAD &&
        currentView.threadUid === currentOptimisticThread.tempThreadUid;

      setOptimisticThread(null);
      if (!shouldRollbackComposer) {
        return;
      }

      setView({
        kind: VIEW.NEW_THREAD,
        pendingPost: currentOptimisticThread.pendingPost,
      });
      setComposerRestoreState({ draft, error });
    },
    [optimisticThreadRef, setOptimisticThread, setView, viewRef],
  );

  const handleBootstrapMessagesHydrated = useCallback(
    (threadUid: string) => {
      setOptimisticThread(previous =>
        previous?.resolvedThreadUid === threadUid ? null : previous,
      );
    },
    [setOptimisticThread],
  );

  const selectedThread =
    view.kind === VIEW.THREAD
      ? threads.find(t => t.uid === view.threadUid)
      : null;
  const selectedOptimisticThread = useMemo(() => {
    if (view.kind !== VIEW.THREAD || !optimisticThread) {
      return null;
    }
    if (view.threadUid === optimisticThread.tempThreadUid) {
      return optimisticThread;
    }
    if (
      optimisticThread.resolvedThreadUid &&
      view.threadUid === optimisticThread.resolvedThreadUid
    ) {
      return optimisticThread;
    }
    return null;
  }, [optimisticThread, view]);

  const isSelectedTemporaryThread = Boolean(
    view.kind === VIEW.THREAD &&
      selectedOptimisticThread &&
      view.threadUid === selectedOptimisticThread.tempThreadUid &&
      !selectedOptimisticThread.resolvedThreadUid,
  );

  const temporaryPostTarget = selectedOptimisticThread
    ? pendingPostTarget(selectedOptimisticThread.pendingPost)
    : undefined;

  const teacherName = leader?.name?.trim() || t("Teacher");
  const showEmptyConversationsState =
    !threadsLoading && threads.length === 0 && !debouncedSearch;

  const sectionInfo = { uid: sectionUid, end_time: sectionEndTime };
  const learnerInfo = {
    uid: currentLearner!.uid,
    name: currentLearner?.name ?? "",
    avatar: currentLearner?.avatar ?? null,
  };
  const viewContext: ThreadViewContext = {
    section: sectionInfo,
    learner: learnerInfo,
    leader: leader ?? null,
  };

  const handleOpenGeneralThread = () => {
    const existingGeneral = threads.find(isGeneralThread);
    clearComposerFlowState();
    if (existingGeneral) {
      setView({
        kind: VIEW.THREAD,
        threadUid: existingGeneral.uid,
        postTitleOverride: null,
      });
    } else {
      setView({ kind: VIEW.NEW_THREAD, pendingPost: { kind: "general" } });
    }
  };

  return (
    <>
      {isMobile ? (
        <Badge badgeContent={unreadCount} color="error">
          <Button
            variant="contained"
            onClick={() => setOpen(true)}
            sx={{
              minWidth: 0,
              width: 48,
              height: 48,
              borderRadius: "50%",
              padding: 0,
            }}
          >
            <Icon icon={faComment} sx={{ fontSize: "1.5rem" }} />
          </Button>
        </Badge>
      ) : (
        <Button
          variant="contained"
          onClick={() => setOpen(true)}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            whiteSpace: "nowrap",
          }}
        >
          <Badge badgeContent={unreadCount} color="error" sx={{ mr: 8 }}>
            <UserHeadshotImage user={leader} size={24} />
          </Badge>
          <Box component="span">{t("All Messages")}</Box>
        </Button>
      )}
      <Drawer
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: (theme: Theme) => ({
            width: 400,
            display: "flex",
            flexDirection: "column",
            [theme.breakpoints.down("sm")]: {
              width: "85%",
            },
          }),
        }}
      >
        {view.kind === VIEW.THREAD ? (
          <ThreadDetailView
            threadUid={view.threadUid}
            viewContext={viewContext}
            postTitle={postTitleFromThreadOrOptimistic(
              selectedThread,
              selectedOptimisticThread,
            )}
            searchQuery={debouncedSearch}
            optimisticMessages={selectedOptimisticThread?.messages ?? []}
            isTemporaryThread={isSelectedTemporaryThread}
            temporaryPostTarget={temporaryPostTarget}
            onBootstrapMessagesHydrated={handleBootstrapMessagesHydrated}
            onBack={() => setView({ kind: VIEW.LIST })}
            onClose={handleClose}
          />
        ) : view.kind === VIEW.NEW_THREAD ? (
          <NewThreadView
            viewContext={viewContext}
            postTarget={pendingPostTarget(view.pendingPost)}
            postTitle={
              view.pendingPost.kind === "post"
                ? view.pendingPost.postTitle
                : undefined
            }
            hideBackButton={false}
            onBack={() => {
              clearComposerFlowState();
              setView({ kind: VIEW.LIST });
            }}
            onClose={handleClose}
            onThreadCreated={handleThreadCreated}
            onOptimisticThreadCreated={handleOptimisticThreadCreated}
            onThreadCreateFailed={handleThreadCreateFailed}
            initialDraft={composerRestoreState?.draft}
            initialError={composerRestoreState?.error}
          />
        ) : (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                p: 12,
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <UserHeadshotImage
                user={leader}
                size={44}
                sx={{ flexShrink: 0 }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle1" noWrap sx={{ fontWeight: 600 }}>
                  {leader?.name ?? t("Teacher")}
                </Typography>
                {Boolean(sectionEndTime) && (
                  <Typography variant="caption" color="text.secondary">
                    {t("Teacher access ends {{date}}", {
                      date: dayjs(sectionEndTime).format("MMM D, YYYY"),
                    })}
                  </Typography>
                )}
              </Box>
              <IconButton
                aria-label={t("Close messages")}
                icon={faTimes}
                variant="text"
                onClick={handleClose}
              />
            </Box>
            {!showEmptyConversationsState && (
              <Box
                sx={{
                  py: 8,
                  px: 12,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <TextField
                  fullWidth
                  size="small"
                  placeholder={t("Search all messages")}
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Icon
                          icon={faSearch}
                          sx={{ color: "text.secondary", fontSize: "1rem" }}
                        />
                      </InputAdornment>
                    ),
                    endAdornment:
                      threadsLoading && debouncedSearch ? (
                        <InputAdornment position="end">
                          <CircularProgress size={16} />
                        </InputAdornment>
                      ) : undefined,
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": { borderRadius: 20 },
                  }}
                />
              </Box>
            )}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                overflowY: "auto",
              }}
            >
              {threadsLoading && threads.length === 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    p: 4,
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : showEmptyConversationsState ? (
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 24,
                    gap: 12,
                  }}
                >
                  <Icon
                    icon={faComment}
                    sx={{ color: "neutral.300", fontSize: "4rem" }}
                  />
                  <Typography variant="h6" color="text.secondary">
                    {t("No conversations yet")}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ textAlign: "center", maxWidth: 300 }}
                  >
                    {t(
                      'Explore a lesson and use the "Ask a question" button below your lesson to start chatting with your teacher.',
                    )}
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={handleOpenGeneralThread}
                    sx={{
                      mt: 8,
                      borderColor: "neutral.200",
                      color: "text.primary",
                      borderRadius: "1.4rem",
                      textTransform: "none",
                    }}
                  >
                    {t("Message {{teacherName}} directly", {
                      teacherName,
                    })}
                  </Button>
                </Box>
              ) : threads.length === 0 ? (
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 2,
                  }}
                >
                  <Typography variant="body1" color="text.secondary">
                    {t("No messages found")}
                  </Typography>
                </Box>
              ) : (
                <>
                  {threads.map(thread => (
                    <ThreadListItem
                      key={thread.uid}
                      thread={thread}
                      teacherName={teacherName}
                      onClick={() => {
                        clearComposerFlowState();
                        setView({
                          kind: VIEW.THREAD,
                          threadUid: thread.uid,
                          postTitleOverride: getThreadPostTitle(thread),
                        });
                      }}
                    />
                  ))}
                  {!debouncedSearch && !threads.some(isGeneralThread) && (
                    <Box
                      data-test-id="ask-anything-row"
                      onClick={handleOpenGeneralThread}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        py: 12,
                        px: 16,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        cursor: "pointer",
                        transition: "background-color 0.15s",
                        "&:hover": { backgroundColor: "action.hover" },
                      }}
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "50%",
                          backgroundColor: "primary.50",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Icon
                          icon={faPlus}
                          sx={{ color: "primary.main", fontSize: "1.2rem" }}
                        />
                      </Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                        {t("Ask {{teacherName}} anything", { teacherName })}
                      </Typography>
                    </Box>
                  )}
                </>
              )}
            </Box>
          </>
        )}
      </Drawer>
    </>
  );
}
